/**
 * usePackageComponents Hook
 *
 * RTKQ-based component management with selectFromResult for granular subscriptions.
 * Uses optimistic updates for reordering.
 */
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useMatter } from '@/hooks/useMatter';
import {
  type ComponentType,
  correspondenceApi,
  type CorrespondenceComponentSchema,
  type CreateCorrespondenceComponent,
  type ReorderComponentsRequest,
  type UpdateCorrespondenceComponent,
  useCorrespondenceComponentCreateMutation,
  useCorrespondenceComponentDestroyMutation,
  useCorrespondenceComponentReorderMutation,
  useCorrespondenceComponentUpdateMutation,
  useCorrespondencePackageRetrieveQuery,
} from '@/services/api/correspondenceService';

// ============================================================================
// Types
// ============================================================================

export interface UsePackageComponentsOptions {
  packageId: string | undefined;
  /** Skip fetching */
  skip?: boolean;
}

type ComponentData = Omit<
  CreateCorrespondenceComponent,
  'component_type' | 'order'
>;

/**
 * Coverage slot selections for a component.
 */
export interface ComponentCoverageUpdate {
  coverage_1p_id?: string | null;
  coverage_3p_id?: string | null;
  coverage_health_id?: string | null;
}

export interface UsePackageComponentsResult {
  // Data
  components: CorrespondenceComponentSchema[];

  // Actions
  addComponent: (type: ComponentType, data?: ComponentData) => Promise<void>;
  addTemplate: (
    templateId: string,
    overrides?: Record<string, string>,
  ) => Promise<void>;
  addBlob: (blobId: string) => Promise<void>;
  addStaticDocument: (staticDocumentId: string) => Promise<void>;
  addMergeDocument: (templateId: string, sourceBlobId: string) => Promise<void>;
  removeComponent: (componentId: string) => Promise<void>;
  updateComponentOverrides: (
    componentId: string,
    overrides: Record<string, string>,
  ) => Promise<void>;
  updateComponentCoverage: (
    componentId: string,
    coverageUpdate: ComponentCoverageUpdate,
  ) => Promise<void>;
  reorderComponents: (componentIds: string[]) => Promise<void>;

  // Loading
  isLoading: boolean;
  isMutating: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function usePackageComponents({
  packageId,
  skip = false,
}: UsePackageComponentsOptions): UsePackageComponentsResult {
  const { matterId } = useMatter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  // ========== RTKQ Query with selectFromResult ==========
  // Only subscribes to components array
  const { components, isLoading } = useCorrespondencePackageRetrieveQuery(
    { matterId, id: packageId || '' },
    {
      skip: skip || !packageId,
      selectFromResult: ({ data, isLoading: loading }) => ({
        // Sort by order field to ensure consistent display
        components: [...(data?.components ?? [])].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0),
        ),
        isLoading: loading,
      }),
    },
  );

  // ========== Mutations ==========
  const [createComponent, { isLoading: isAdding }] =
    useCorrespondenceComponentCreateMutation();
  const [destroyComponent, { isLoading: isRemoving }] =
    useCorrespondenceComponentDestroyMutation();
  const [patchComponent, { isLoading: isUpdating }] =
    useCorrespondenceComponentUpdateMutation();
  const [reorderMutation, { isLoading: isReordering }] =
    useCorrespondenceComponentReorderMutation();

  const isMutating = isAdding || isRemoving || isUpdating || isReordering;

  // Base request args
  const baseArgs = useMemo(
    () => ({ matterId, packageId: packageId || '' }),
    [matterId, packageId],
  );

  // ========== Actions ==========
  const addComponent = useCallback(
    async (type: ComponentType, data: ComponentData = {}) => {
      if (!packageId) return;

      const component: CreateCorrespondenceComponent = {
        component_type: type,
        order: components.length,
        ...data,
      };

      // Generate a temporary ID for optimistic update
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      // Optimistic update: immediately add component with 'generating' status
      const patchResult = dispatch(
        correspondenceApi.util.updateQueryData(
          'correspondencePackageRetrieve',
          { matterId, id: packageId },
          (draft) => {
            if (!draft.components) {
              draft.components = [];
            }
            // Add a placeholder component
            draft.components.push({
              id: tempId,
              package_id: packageId,
              component_type: type,
              order: draft.components.length,
              status: 'generating',
              v2_template_id: data.v2_template_id,
              blob_id: data.blob_id,
              overrides: data.overrides ?? {},
              template: undefined,
              blob: undefined,
              rendered_pdf_url: undefined,
              error_message: undefined,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as CorrespondenceComponentSchema);
            // Also set package status to generating
            draft.status = 'generating';
          },
        ),
      );

      try {
        await createComponent({
          ...baseArgs,
          createCorrespondenceComponent: component,
        }).unwrap();
        // Service config handles tag invalidation
      } catch (error) {
        // Rollback on error
        patchResult.undo();
        throw error;
      }
    },
    [
      createComponent,
      packageId,
      components.length,
      baseArgs,
      dispatch,
      matterId,
    ],
  );

  const addTemplate = useCallback(
    (templateId: string, overrides?: Record<string, string>) =>
      addComponent('template', { v2_template_id: templateId, overrides }),
    [addComponent],
  );

  const addBlob = useCallback(
    (blobId: string) => addComponent('matter_blob', { blob_id: blobId }),
    [addComponent],
  );

  const addStaticDocument = useCallback(
    (staticDocumentId: string) =>
      addComponent('static_document', { static_document_id: staticDocumentId }),
    [addComponent],
  );

  const addMergeDocument = useCallback(
    (templateId: string, sourceBlobId: string) =>
      addComponent('merge_document', {
        v2_template_id: templateId,
        merge_source_blob_id: sourceBlobId,
      }),
    [addComponent],
  );

  const removeComponent = useCallback(
    async (componentId: string) => {
      if (!packageId) return;

      // Optimistic update: immediately remove from cache
      const patchResult = dispatch(
        correspondenceApi.util.updateQueryData(
          'correspondencePackageRetrieve',
          { matterId, id: packageId },
          (draft) => {
            if (!draft.components) return;
            const index = draft.components.findIndex(
              (c) => c.id === componentId,
            );
            if (index !== -1) {
              draft.components.splice(index, 1);
              // Re-number remaining components
              draft.components.forEach((c, i) => {
                c.order = i;
              });
            }
          },
        ),
      );

      try {
        await destroyComponent({
          ...baseArgs,
          id: componentId,
        }).unwrap();
      } catch (error) {
        // Rollback on error
        patchResult.undo();
        throw error;
      }
    },
    [destroyComponent, packageId, baseArgs, dispatch, matterId],
  );

  const updateComponentOverrides = useCallback(
    async (componentId: string, overrides: Record<string, string>) => {
      if (!packageId) return;

      // Optimistic update: immediately update overrides, clear missing_fields, and set status
      const patchResult = dispatch(
        correspondenceApi.util.updateQueryData(
          'correspondencePackageRetrieve',
          { matterId, id: packageId },
          (draft) => {
            if (!draft.components) return;
            const component = draft.components.find(
              (c) => c.id === componentId,
            );
            if (component) {
              component.overrides = overrides;
              component.missing_fields = {}; // Clear missing fields optimistically
              component.status = 'queued_for_render';
            }
            // Also update package status
            draft.status = 'queued_for_render';
          },
        ),
      );

      try {
        const data: UpdateCorrespondenceComponent = { overrides };
        await patchComponent({
          ...baseArgs,
          id: componentId,
          patchedUpdateCorrespondenceComponent: data,
        }).unwrap();
        // Service config handles tag invalidation
      } catch (error) {
        // Rollback on error
        patchResult.undo();
        throw error;
      }
    },
    [patchComponent, packageId, baseArgs, dispatch, matterId],
  );

  const reorderComponents = useCallback(
    async (componentIds: string[]) => {
      if (!packageId) return;

      // Optimistic update - instantly reorder in cache
      const patchResult = dispatch(
        correspondenceApi.util.updateQueryData(
          'correspondencePackageRetrieve',
          { matterId, id: packageId },
          (draft) => {
            if (!draft.components) return;

            // Create a map of id -> component
            const componentMap = new Map(
              draft.components.map((c) => [c.id, c]),
            );

            // Reorder based on new order - update in place
            const reordered: typeof draft.components = [];
            componentIds.forEach((id, index) => {
              const component = componentMap.get(id);
              if (component) {
                component.order = index;
                reordered.push(component);
              }
            });
            draft.components = reordered;
          },
        ),
      );

      try {
        const request: ReorderComponentsRequest = {
          components: componentIds.map((id, index) => ({
            component_id: id,
            order: index,
          })),
        };

        await reorderMutation({
          ...baseArgs,
          reorderComponentsRequest: request,
        }).unwrap();
      } catch {
        // Rollback on error
        patchResult.undo();
        throw new Error('Failed to reorder components');
      }
    },
    [dispatch, reorderMutation, matterId, packageId, baseArgs],
  );

  const updateComponentCoverage = useCallback(
    async (componentId: string, coverageUpdate: ComponentCoverageUpdate) => {
      if (!packageId) return;

      // Optimistic update: immediately update coverage IDs and mark for re-render
      const patchResult = dispatch(
        correspondenceApi.util.updateQueryData(
          'correspondencePackageRetrieve',
          { matterId, id: packageId },
          (draft) => {
            if (!draft.components) return;
            const component = draft.components.find(
              (c) => c.id === componentId,
            );
            if (component) {
              // Apply coverage updates to the component
              if ('coverage_1p_id' in coverageUpdate) {
                component.coverage_1p_id = coverageUpdate.coverage_1p_id;
              }
              if ('coverage_3p_id' in coverageUpdate) {
                component.coverage_3p_id = coverageUpdate.coverage_3p_id;
              }
              if ('coverage_health_id' in coverageUpdate) {
                component.coverage_health_id = coverageUpdate.coverage_health_id;
              }
              component.status = 'queued_for_render';
              component.needs_rerender = true;
            }
            // Also update package status
            draft.status = 'queued_for_render';
          },
        ),
      );

      try {
        // Send update to API - coverage fields will be included in the patch
        const data: UpdateCorrespondenceComponent = {
          ...coverageUpdate,
        };
        await patchComponent({
          ...baseArgs,
          id: componentId,
          patchedUpdateCorrespondenceComponent: data,
        }).unwrap();
      } catch (error) {
        // Rollback on error
        patchResult.undo();
        throw error;
      }
    },
    [patchComponent, packageId, baseArgs, dispatch, matterId],
  );

  return {
    components,
    addComponent,
    addTemplate,
    addBlob,
    addStaticDocument,
    addMergeDocument,
    removeComponent,
    updateComponentOverrides,
    updateComponentCoverage,
    reorderComponents,
    isLoading,
    isMutating,
  };
}
